/* ==========================================================================
   Localization — country picker, currency detection, price rendering

   The storefront is built for Shopify Markets: each market has its own
   price list, and the country you pick decides both the currency and the
   prices. markets.js holds the price lists (USD only at launch); this module
   picks a country, renders the picker, and writes prices into the page.

   Any element carrying data-price="<handle>" gets the current market's price;
   data-price-compare="<handle>" gets the compare-at price and hides itself
   when the market has none. Shopify Liquid will render those server-side
   later — the data-attributes mark exactly which nodes become `| money`.
   ========================================================================== */

window.STORE = window.STORE || {};

STORE.localization = (function () {
  'use strict';

  var M = STORE.markets;
  var STORE_KEY = 'store_country';
  var LANG_KEY = 'store_language';

  var state = { country: null, language: 'en' };
  var listeners = [];

  /* ---------- storage (private mode can throw on both read and write) ---- */

  function read(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function write(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* no-op */ }
  }

  /* ---------- picking a country ----------------------------------------- */

  /* Guess from the browser's region subtag (en-GB -> GB), then from the
     timezone's region, then fall back to the default market. Nothing here
     touches the network; Shopify does this server-side by IP. */
  function detectCountry() {
    var langs = navigator.languages || [navigator.language || ''];
    for (var i = 0; i < langs.length; i++) {
      var region = String(langs[i]).split('-')[1];
      if (region && M.countries[region.toUpperCase()]) return region.toUpperCase();
    }
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      var city = tz.split('/')[1];
      if (city) {
        for (var code in M.countries) {
          if (M.countries[code][0].replace(/\s/g, '') === city.replace(/_/g, '')) return code;
        }
      }
    } catch (e) { /* Intl unavailable */ }
    return null;
  }

  function resolveCountry() {
    var saved = read(STORE_KEY);
    if (saved && M.countries[saved]) return saved;
    return detectCountry() || M.defaultCountry;
  }

  /* ---------- money ------------------------------------------------------ */

  function table() {
    var entry = M.countries[state.country] || M.countries[M.defaultCountry];
    return entry[1];
  }

  function currencyCode() {
    return table().currency;
  }

  /* Format a decimal string the way the storefront renders it for this
     currency: "59.99" in USD -> "$59.99". */
  function formatMoney(value, code) {
    code = code || currencyCode();
    var rules = M.currencies[code];
    if (!rules) return value;
    var n = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(n)) return value;
    var fixed = n.toFixed(rules.decimals);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, rules.thousands);
    var body = parts.length > 1 ? whole + rules.decimal + parts[1] : whole;
    return rules.position === 'post'
      ? body + rules.gap + rules.symbol
      : rules.symbol + rules.gap + body;
  }

  function priceOf(handle) {
    var row = table().prices[handle];
    return row ? row[0] : null;
  }

  function compareAtOf(handle) {
    var row = table().prices[handle];
    return row ? row[1] : null;
  }

  /* ---------- free shipping --------------------------------------------- */

  /* Reads the free-shipping rule for the active market from markets.js.
     Every market currently ships free ({ free: true }). */
  function freeShipping() {
    var rules = M.freeShipping || {};
    return rules[state.country] || rules._default || { claim: false };
  }

  /* ---------- rendering -------------------------------------------------- */

  function renderPrices(root) {
    root = root || document;
    var code = currencyCode();

    Array.prototype.forEach.call(root.querySelectorAll('[data-price]'), function (el) {
      var p = priceOf(el.getAttribute('data-price'));
      if (p !== null) el.textContent = formatMoney(p, code);
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-price-compare]'), function (el) {
      var p = compareAtOf(el.getAttribute('data-price-compare'));
      if (p) {
        el.textContent = formatMoney(p, code);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    /* buttons that quote the price inside their own label */
    Array.prototype.forEach.call(root.querySelectorAll('[data-price-label]'), function (el) {
      var handle = el.getAttribute('data-price-label');
      var p = priceOf(handle);
      if (p === null) return;
      var label = el.getAttribute('data-label-add') || 'Add to cart';
      var arrow = el.hasAttribute('data-label-arrow') ? ' →' : '';
      el.textContent = label + ' — ' + formatMoney(p, code) + arrow;
    });

    /* Product schema follows the active Shopify Market too. Keeping price
       out of static HTML prevents a default value from becoming a false
       quote after the visitor changes country. */
    if (root === document) {
      var form = document.querySelector('[data-product-form]');
      var handle = form && form.getAttribute('data-product-form');
      var rawPrice = handle && priceOf(handle);
      if (handle && rawPrice !== null) {
        Array.prototype.forEach.call(document.querySelectorAll('script[type="application/ld+json"]'), function (script) {
          try {
            var schema = JSON.parse(script.textContent);
            if (schema['@type'] !== 'Product' || !schema.offers || !schema.offers[0]) return;
            schema.offers[0].priceCurrency = code;
            schema.offers[0].price = parseFloat(rawPrice);
            script.textContent = JSON.stringify(schema);
          } catch (e) { /* leave unrelated schema untouched */ }
        });
      }
    }
  }

  function currentLabel() {
    return currencyCode() + ' · ' + state.language.toUpperCase();
  }

  function buildPanel(loc, idSuffix) {
    var panel = loc.querySelector('[data-loc-panel]');
    if (!panel || panel.dataset.built) return;
    panel.dataset.built = '1';

    var html = [];
    html.push('<div class="loc__form">');
    html.push('<p class="loc__label" id="loc-lang-' + idSuffix + '">Language</p>');
    html.push('<ul class="loc__list" role="list" aria-labelledby="loc-lang-' + idSuffix + '">');
    M.languages.forEach(function (l) {
      var on = l.code === state.language;
      html.push('<li><button type="button" class="loc__opt' + (on ? ' is-active' : '') +
        '" lang="' + l.code + '" data-set-language="' + l.code + '">' + l.label +
        (on ? check() : '') + '</button></li>');
    });
    html.push('</ul></div>');

    html.push('<div class="loc__form">');
    html.push('<p class="loc__label" id="loc-country-' + idSuffix + '">Country / Region</p>');
    html.push('<ul class="loc__list loc__list--scroll" role="list" aria-labelledby="loc-country-' +
      idSuffix + '">');
    Object.keys(M.countries).sort(function (a, b) {
      return M.countries[a][0].localeCompare(M.countries[b][0]);
    }).forEach(function (code) {
      var entry = M.countries[code];
      var on = code === state.country;
      html.push('<li><button type="button" class="loc__opt' + (on ? ' is-active' : '') +
        '" data-set-country="' + code + '">' +
        '<span class="loc__cname">' + entry[0] + '</span>' +
        '<span class="loc__cur">' + entry[1].currency + ' ' +
        (M.currencies[entry[1].currency] || {}).symbol + '</span>' +
        '</button></li>');
    });
    html.push('</ul></div>');

    panel.innerHTML = html.join('');
  }

  function check() {
    return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
      ' stroke-width="2.4" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
  }

  function refreshPickers() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-loc]'), function (loc, i) {
      var label = loc.querySelector('.loc__current');
      if (label) label.textContent = currentLabel();
      var panel = loc.querySelector('[data-loc-panel]');
      if (panel && panel.dataset.built) {
        panel.dataset.built = '';
        panel.innerHTML = '';
        buildPanel(loc, i === 0 ? 'header' : 'mobile');
      }
    });
  }

  /* ---------- public ----------------------------------------------------- */

  function setCountry(code) {
    if (!M.countries[code] || code === state.country) return;
    state.country = code;
    write(STORE_KEY, code);
    renderPrices();
    refreshPickers();
    listeners.forEach(function (fn) { fn(state); });
  }

  function setLanguage(code) {
    state.language = code;
    write(LANG_KEY, code);
    refreshPickers();
  }

  function onChange(fn) { listeners.push(fn); }

  function init() {
    state.country = resolveCountry();
    var lang = read(LANG_KEY);
    if (lang) state.language = lang;

    renderPrices();

    Array.prototype.forEach.call(document.querySelectorAll('[data-loc]'), function (loc, i) {
      var toggle = loc.querySelector('[data-loc-toggle]');
      var panel = loc.querySelector('[data-loc-panel]');
      if (!toggle || !panel) return;

      var label = loc.querySelector('.loc__current');
      if (label) label.textContent = currentLabel();

      function close() {
        loc.classList.remove('is-open');
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      }
      function open() {
        buildPanel(loc, i === 0 ? 'header' : 'mobile');
        loc.classList.add('is-open');
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      }

      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        if (loc.classList.contains('is-open')) close(); else open();
      });
      document.addEventListener('click', function (e) {
        if (!loc.contains(e.target)) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });
      panel.addEventListener('click', function (e) {
        var c = e.target.closest('[data-set-country]');
        if (c) { setCountry(c.getAttribute('data-set-country')); close(); return; }
        var l = e.target.closest('[data-set-language]');
        if (l) { setLanguage(l.getAttribute('data-set-language')); close(); }
      });
    });
  }

  return {
    init: init,
    formatMoney: formatMoney,
    priceOf: priceOf,
    compareAtOf: compareAtOf,
    currencyCode: currencyCode,
    freeShipping: freeShipping,
    renderPrices: renderPrices,
    setCountry: setCountry,
    onChange: onChange,
    get country() { return state.country; },
  };
}());
